import { KInput, useKeyboardInput } from './Keyboard';
import { useMemo, useState } from 'react';
import type { Locale } from '@360face/domain';
import type { Messages } from '../i18n/messages';
import {
  cleanNational,
  countryOptions,
  dialCode,
  examplePhone,
  flagOf,
  formatNational,
  searchCountries,
  type CountryCode,
} from './phone';

/**
 * Mobile number with a country picker (flag + code, Portugal first). The field shows the number
 * formatted the way it is written in that country and stores digits only; the caller turns country + digits into E.164 with `toE164`.
 */
export function PhoneInput({
  country,
  value,
  onChange,
  locale,
  m,
  big = false,
  invalid = false,
  label,
}: {
  country: CountryCode;
  value: string;
  onChange: (next: { country?: CountryCode; phone?: string }) => void;
  locale: Locale;
  m: Messages;
  big?: boolean;
  invalid?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const all = useMemo(() => countryOptions(locale), [locale]);
  // On-screen keyboard: types the national number as plain digits (formatting is display only).
  const digitsKb = useKeyboardInput('digits', value, (v) => onChange({ phone: cleanNational(v) }), 15, {
    inline: true,
  });
  const shown = searchCountries(all, query);

  return (
    <div className={big ? 'phone big' : 'phone'}>
      <button
        type="button"
        className="cc"
        aria-haspopup="dialog"
        aria-label={`${m.countryTitle}: ${dialCode(country)}`}
        onClick={() => setOpen(true)}
      >
        <span aria-hidden>{flagOf(country)}</span>
        <b>{dialCode(country)}</b>
      </button>
      <input
        className={invalid ? 'input invalid' : 'input'}
        {...digitsKb}
        autoComplete="off"
        aria-label={label ?? m.fields.phone}
        value={formatNational(country, value)}
        onChange={(e) => {
          const next = cleanNational(e.target.value);
          // Backspace over a formatting character ("(", ")", space) removes the digit before it.
          const deletedFormatting = next === value && e.target.value.length < formatNational(country, value).length;
          onChange({ phone: deletedFormatting ? value.slice(0, -1) : next });
        }}
        placeholder={examplePhone(country)}
      />

      {open && (
        <div className="sheet-backdrop" onClick={() => setOpen(false)}>
          <div
            className="sheet cc-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={m.countryTitle}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="k-h2">{m.countryTitle}</h2>
            <KInput
              mode="text"
              max={30}
              className="input"
              placeholder={m.countrySearch}
              value={query}
              onValue={setQuery}
            />
            <div className="cc-list">
              {shown.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  className="cc-opt"
                  aria-pressed={c.code === country}
                  onClick={() => {
                    onChange({ country: c.code });
                    setQuery('');
                    setOpen(false);
                  }}
                >
                  <span aria-hidden>{c.flag}</span>
                  <span>{c.name}</span>
                  <b>{c.dial}</b>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
